import {
  ApiError,
  ConflictError,
  ForbiddenError,
  FulfillmentError,
  NotFoundError,
} from '../../api/errors';
import { fulfillmentReasonMessages } from '../../domain/fulfillment';
import { itemStatuses, type ItemStatus } from '../../domain/inventory';
import { statusLabels } from '../../domain/status';

const statusPattern = new RegExp(`\\b(${itemStatuses.join('|')})\\b`, 'g');

/**
 * The server's detail for an error that names what failed, in words: item
 * statuses as their labels, and a stock line's label dropped where the rest
 * already names the stock item (tidaltech/koi#8467). Only the details of
 * reasons the server words for people are shown.
 */
function readableDetail(error: FulfillmentError): string {
  if (error.reason !== 'ITEM_NOT_AVAILABLE' && error.reason !== 'INSUFFICIENT_STOCK') return '';
  let detail = error.detail.trim();
  const line = /^line "([^"]*)": (.*)$/s.exec(detail);
  if (line && (line[1] === '' || line[2]?.startsWith(line[1] ?? ''))) detail = line[2] ?? '';
  return detail.replace(statusPattern, (status) =>
    statusLabels[status as ItemStatus].toLowerCase(),
  );
}

/**
 * The serials an ITEM_NOT_AVAILABLE error names, e.g. at handover:
 * "VVCS-0001 (Camera) is now SCRAP; VVCS-0002 is now NEEDS_REPAIR".
 */
export function unavailableItemIds(error: unknown): string[] {
  if (!(error instanceof FulfillmentError) || error.reason !== 'ITEM_NOT_AVAILABLE') return [];
  return error.detail
    .split(';')
    .map((part) => /^\s*(\S+)/.exec(part)?.[1] ?? '')
    .filter(Boolean);
}

/** Operator-facing text for an API error; never the raw server message. */
export function fulfillmentErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof FulfillmentError) {
    const detail = readableDetail(error);
    if (error.reason === 'ITEM_NOT_AVAILABLE' && detail) {
      // At handover the server re-checks every picked item ("is now"); a
      // scan is refused before the item is on the order.
      return / is now /.test(error.detail)
        ? `These picked items can't ship: ${detail}. Remove them, pick replacements, then finish packing again.`
        : `That item can't ship: ${detail}. Pick another.`;
    }
    if (error.reason === 'INSUFFICIENT_STOCK' && detail) {
      return `There is not enough available stock: ${detail}.`;
    }
    return fulfillmentReasonMessages[error.reason];
  }
  if (error instanceof ConflictError) {
    return 'Someone else changed this order. Reload it to see the latest picks and try again.';
  }
  if (error instanceof ForbiddenError) return 'You do not have permission to do this.';
  if (error instanceof NotFoundError) return 'This order no longer exists.';
  if (error instanceof ApiError && error.code === 'NETWORK') {
    return 'The server could not be reached. Check the connection and try again.';
  }
  return fallback;
}

/** True when a request may or may not have been applied. */
export function isUnknownOutcome(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'NETWORK';
}
